import type { PagedResult } from '@domain/models/paged-result.model';
import type {
  ActiveQuestion,
  AgoraToken,
  CreateStreamSessionResult,
  LeaderboardEntry,
  OpenQuestionResult,
  QuizQuestion,
  StreamPrizeTier,
  StreamQuestion,
  StreamQuestionResults,
  StreamSession,
} from '@domain/models/streaming.model';

import type { ApiPagedResponse } from '@data/dto/api.dto';
import type {
  ActiveQuestionDto,
  AgoraTokenDto,
  CreateStreamSessionDto,
  LeaderboardEntryDto,
  OpenQuestionDto,
  QuizQuestionDto,
  StreamPrizeTierDto,
  StreamQuestionDto,
  StreamQuestionResultsDto,
  StreamSessionDto,
} from '@data/dto/streaming.dto';

export function mapStreamSession(dto: StreamSessionDto): StreamSession {
  return {
    streamSessionId: dto.streamSessionId ?? 0,
    title: dto.title ?? '',
    agoraChannelName: dto.agoraChannelName ?? '',
    status: dto.status ?? 'Scheduled',
    hostUserId: dto.hostUserId ?? 0,
    scheduledAt: dto.scheduledAt ?? null,
    startedAt: dto.startedAt ?? null,
    endedAt: dto.endedAt ?? null,
    questionsCount: dto.questionsCount ?? 0,
  };
}

export function mapCreateStreamSession(dto: CreateStreamSessionDto): CreateStreamSessionResult {
  return {
    streamSessionId: dto.streamSessionId ?? 0,
    agoraChannelName: dto.agoraChannelName ?? '',
    hostUserId: dto.hostUserId ?? 0,
  };
}

export function mapAgoraToken(dto: AgoraTokenDto): AgoraToken {
  return {
    appId: dto.appId ?? '',
    channelName: dto.channelName ?? '',
    uid: dto.uid ?? 0,
    token: dto.token ?? '',
    expireAt: dto.expireAt ?? 0,
    role: dto.role ?? '',
  };
}

export function mapQuizQuestion(dto: QuizQuestionDto): QuizQuestion {
  return {
    questionId: dto.questionId ?? 0,
    text: dto.text ?? '',
    type: dto.type ?? 'MultipleChoice',
    durationSeconds: dto.durationSeconds ?? 20,
    points: dto.points ?? 100,
    options: (dto.options ?? []).map((o) => ({
      questionOptionId: o.questionOptionId ?? null,
      text: o.text ?? '',
      isCorrect: !!o.isCorrect,
      displayOrder: o.displayOrder ?? 0,
    })),
  };
}

export function mapPagedQuizQuestions(
  response: ApiPagedResponse<QuizQuestionDto>,
): PagedResult<QuizQuestion> {
  return {
    items: (response.items ?? []).map(mapQuizQuestion),
    total: response.total ?? 0,
    page: response.page ?? 1,
    pageSize: response.limit ?? 20,
  };
}

export function mapStreamQuestion(dto: StreamQuestionDto): StreamQuestion {
  return {
    streamQuestionId: dto.streamQuestionId ?? 0,
    questionId: dto.questionId ?? 0,
    text: dto.text ?? '',
    type: dto.type ?? '',
    durationSeconds: dto.durationSeconds ?? 0,
    points: dto.points ?? 0,
    displayOrder: dto.displayOrder ?? 0,
    status: dto.status ?? 'Pending',
    openedAt: dto.openedAt ?? null,
    closesAt: dto.closesAt ?? null,
    closedAt: dto.closedAt ?? null,
    answersCount: dto.answersCount ?? 0,
  };
}

export function mapOpenQuestion(dto: OpenQuestionDto): OpenQuestionResult {
  return {
    streamQuestionId: dto.streamQuestionId ?? 0,
    openedAt: dto.openedAt ?? '',
    closesAt: dto.closesAt ?? '',
  };
}

export function mapActiveQuestion(dto: ActiveQuestionDto | null): ActiveQuestion | null {
  if (!dto) return null;
  return {
    streamQuestionId: dto.streamQuestionId ?? 0,
    questionId: dto.questionId ?? 0,
    text: dto.text ?? '',
    type: dto.type ?? '',
    closesAt: dto.closesAt ?? '',
    remainingSeconds: dto.remainingSeconds ?? 0,
    options: (dto.options ?? []).map((o) => ({
      questionOptionId: o.questionOptionId ?? 0,
      text: o.text ?? '',
      displayOrder: o.displayOrder ?? 0,
    })),
  };
}

export function mapStreamQuestionResults(dto: StreamQuestionResultsDto): StreamQuestionResults {
  return {
    streamQuestionId: dto.streamQuestionId ?? 0,
    questionText: dto.questionText ?? '',
    status: dto.status ?? '',
    totalAnswers: dto.totalAnswers ?? 0,
    correctAnswers: dto.correctAnswers ?? 0,
    options: (dto.options ?? []).map((o) => ({
      questionOptionId: o.questionOptionId ?? 0,
      text: o.text ?? '',
      isCorrect: !!o.isCorrect,
      answersCount: o.answersCount ?? 0,
      percentage: o.percentage ?? 0,
    })),
  };
}

export function mapLeaderboardEntry(dto: LeaderboardEntryDto): LeaderboardEntry {
  return {
    rank: dto.rank ?? 0,
    userId: dto.userId ?? 0,
    userName: dto.userName ?? '',
    totalPoints: dto.totalPoints ?? 0,
    correctAnswers: dto.correctAnswers ?? 0,
    answersCount: dto.answersCount ?? 0,
    prizeAmount: dto.prizeAmount ?? 0,
  };
}

export function mapPagedLeaderboard(
  response: ApiPagedResponse<LeaderboardEntryDto>,
): PagedResult<LeaderboardEntry> {
  return {
    items: (response.items ?? []).map(mapLeaderboardEntry),
    total: response.total ?? 0,
    page: response.page ?? 1,
    pageSize: response.limit ?? 50,
  };
}

export function mapPrizeTier(dto: StreamPrizeTierDto): StreamPrizeTier {
  return {
    fromRank: dto.fromRank ?? 0,
    toRank: dto.toRank ?? 0,
    prizeAmount: dto.prizeAmount ?? 0,
  };
}
